import { useLoadIElements } from "@/hooks/use-load-ielements";
import { useAppSelector } from "@/store/store-hooks";
import type { GUID } from "@faro-lotv/foundation";
import { selectIElement } from "@faro-lotv/project-source";
import { useCallback, useState } from "react";

type UseLoadIElementOnDemandApi = {
  /** Whether the current iElement is loaded */
  isLoaded: boolean;

  /** Whether the iElement is currently being fetched */
  isLoading: boolean;

  /** Callback to start loading the iElement. Can be called multiple times, but only triggers the loading once. */
  startLoadIfNeeded(): void;
};

/**
 * @returns A function to load an iElement (with subtree) on demand,
 * and booleans indicating whether the iElement is currently loaded or loading.
 * @param ancestorId The ancestor ID to load the iElement and subtree for.
 */
export function useLoadIElementsOnDemand(
  ancestorId?: GUID | null,
): UseLoadIElementOnDemandApi {
  const isLoaded = !!useAppSelector(selectIElement(ancestorId));
  const [shouldLoad, setShouldLoad] = useState(false);
  const isLoading = useLoadIElements(
    shouldLoad && ancestorId ? [{ ancestorIds: [ancestorId] }] : [],
  );

  const startLoadIfNeeded = useCallback(() => {
    if (!isLoaded) {
      setShouldLoad(true);
    }
  }, [isLoaded]);

  return { isLoaded, isLoading, startLoadIfNeeded };
}
